import { Dispatch } from 'react';
import { CountryCode, ProductCodeKey, SubscriptionPlan, Variant } from '@dayetopia/library';
import {
  CBD_REGULAR_TAMPON_KEY,
  CBD_REGULAR_TAMPON_NAME,
  CBD_SUPER_TAMPON_KEY,
  CBD_SUPER_TAMPON_NAME,
  LINER_PAD_KEY,
  LINER_PAD_PRICE_DEFAULT,
  NAKED_REGULAR_TAMPON_KEY,
  NAKED_REGULAR_TAMPON_NAME,
  NAKED_SUPER_TAMPON_KEY,
  NAKED_SUPER_TAMPON_NAME,
  NIGHT_PAD_KEY,
  NIGHT_PLUS_PAD_KEY,
  REGULAR_PAD_KEY,
  AppState,
  PAD_STACK_PRODUCT_IDS,
  MainContextActionTypes,
  TAMPON_BOX_18_NUDE_REGULAR_KEY,
  TAMPON_BOX_9_CBD_REGULAR_KEY,
  TAMPON_BOX_18_NUDE_SUPER_KEY,
  TAMPON_BOX_9_CBD_SUPER_KEY,
  TAMPON_BOX_9_NUDE_REGULAR_KEY,
  TAMPON_BOX_9_NUDE_SUPER_KEY,
  TAMPON_BOX_9_NUDE_SUPER_9_CBD_SUPER_KEY,
  TAMPON_BOX_9_NUDE_REGULAR_9_CBD_REGULAR_KEY
} from '@constants';
import {
  BoxState,
  CartItem,
  CartItemType,
  Prices,
  BoxItem,
  CurrencyObj,
  PadsBoxState,
  PadsBoxStackState,
  PadSizeType,
  CartItems,
  CartItemGiftType,
  SubscriptionCartItem,
  IntervalUnit,
  TFunction,
  TamponBoxStackState
} from '@contracts';
import { getDBProductName } from '@data/products';
import { getBoxPrice } from '@utils/boxPrices';
import { isHsaFsaAvailable } from './flexHsaFsa';
import { getDependantFreeGifts, getFreeGiftsForPromotion } from './freeGift';
import { setOnLocalStorage, getFromLocalStorage } from './localStorage';
import { convertPadsStackBoxItems, isBoxStatePadsStackBox, isPadsAndStackBox } from './pads';
import { calculateTotalPrice } from './price';
import { normalizeRetailTamponBoxVariants } from './tamponBoxes';
import { addToCartTrack, removeFromCartTrack } from './tracking';

const getBoxItems = (boxState: BoxState, prices?: Prices) => {
  if (!boxState) return [];
  if (!prices) return Object.keys(boxState).map((key) => ({ count: boxState[key], name: key }));

  const { cbdsuper, cbdregular, nakedsuper, nakedregular } = boxState;
  const result = [
    { count: cbdsuper, key: CBD_SUPER_TAMPON_KEY, name: CBD_SUPER_TAMPON_NAME, price: prices.cbdTamponPrice },
    { count: cbdregular, key: CBD_REGULAR_TAMPON_KEY, name: CBD_REGULAR_TAMPON_NAME, price: prices.cbdTamponPrice },
    { count: nakedsuper, key: NAKED_SUPER_TAMPON_KEY, name: NAKED_SUPER_TAMPON_NAME, price: prices.nakedTamponPrice },
    { count: nakedregular, key: NAKED_REGULAR_TAMPON_KEY, name: NAKED_REGULAR_TAMPON_NAME, price: prices.nakedTamponPrice }
  ];

  const pr = boxState?.pr || 0;
  const pn = boxState?.pn || 0;
  const pnp = boxState?.pnp || 0;
  const pl = boxState?.pl || 0;

  if (pr > 0) result.push({ count: pr, key: REGULAR_PAD_KEY, name: REGULAR_PAD_KEY, price: prices.regularPadPrice });
  if (pn > 0) result.push({ count: pn, key: NIGHT_PAD_KEY, name: NIGHT_PAD_KEY, price: prices.nightPadPrice });
  if (pnp > 0) result.push({ count: pnp, key: NIGHT_PLUS_PAD_KEY, name: NIGHT_PLUS_PAD_KEY, price: prices.nightPlusPadPrice });
  if (pl > 0) result.push({ count: pl, key: LINER_PAD_KEY, name: LINER_PAD_KEY, price: prices.linerPadPrice });

  return result;
};

const getPadsBoxItems = (boxState: PadsBoxState, prices?: Prices) => {
  const { pr, pn, pnp, pl } = boxState;

  return [
    { count: pr, key: REGULAR_PAD_KEY, name: REGULAR_PAD_KEY, price: prices?.regularPadPrice ?? 0 },
    { count: pn, key: NIGHT_PAD_KEY, name: NIGHT_PAD_KEY, price: prices?.nightPadPrice ?? 0 },
    { count: pnp, key: NIGHT_PLUS_PAD_KEY, name: NIGHT_PLUS_PAD_KEY, price: prices?.nightPlusPadPrice ?? 0 },
    { count: pl, key: LINER_PAD_KEY, name: LINER_PAD_KEY, price: prices?.linerPadPrice ?? 0 }
  ];
};

const getPadsStackBoxItems = (boxState: PadsBoxStackState, prices?: Prices) => {
  const { pr, pn, pnp, pl } = boxState;

  return [
    { count: pl, key: LINER_PAD_KEY, name: LINER_PAD_KEY, price: prices?.linerPadPrice ?? 0 },
    { count: pr, key: REGULAR_PAD_KEY, name: REGULAR_PAD_KEY, price: prices?.regularPadPrice ?? 0 },
    { count: pn, key: NIGHT_PAD_KEY, name: NIGHT_PAD_KEY, price: prices?.nightPadPrice ?? 0 },
    { count: pnp, key: NIGHT_PLUS_PAD_KEY, name: NIGHT_PLUS_PAD_KEY, price: prices?.nightPlusPadPrice ?? 0 }
  ];
};

const getTamponBoxStackItems = (boxState: TamponBoxStackState, prices?: Prices) => {
  const normalized = normalizeRetailTamponBoxVariants(boxState);

  return Object.entries(normalized)
    .filter(([, count]) => count > 0)
    .map(([variantKey, count]) => ({
      count,
      key: variantKey as ProductCodeKey,
      name: variantKey as ProductCodeKey,
      price: prices?.[variantKey as ProductCodeKey] ?? 0
    }));
};

const generatePadsCartIdSuffix = (boxState: PadsBoxState) => {
  const { pr, pn, pnp, pl } = boxState;
  return `${pr}${pn}${pnp}${pl}`;
};

const isCartItemUnique = (productCode: ProductCodeKey) => {
  if (productCode?.includes('ppc_')) {
    return true;
  }
  return false;
};

// Helper function to create a cart item
const createCartItemHelper = (
  productCode: ProductCodeKey,
  quantity: number,
  price: number,
  editable: boolean,
  type: CartItemType,
  boxItems?: BoxItem[],
  cartIdSuffix?: string,
  subscriptionPlans?: SubscriptionPlan[],
  selectedPlan?: SubscriptionPlan,
  prices?: Prices
): CartItem => {
  switch (type) {
    case CartItemType.Consultation: {
      const item = new CartItem(productCode, productCode, price, quantity, CartItemType.Consultation, editable);
      item.unique = isCartItemUnique(productCode);
      return item;
    }
    case CartItemType.OneOffPurchase: {
      if (productCode === ProductCodeKey.TamponBox || productCode === ProductCodeKey.Pads) {
        const item = new CartItem(
          cartIdSuffix ? `${productCode}_${cartIdSuffix}` : productCode,
          productCode,
          price,
          quantity,
          type,
          editable,
          undefined,
          undefined,
          isCartItemUnique(productCode),
          undefined,
          undefined,
          undefined,
          boxItems
        );
        return item;
      }

      let finalProductCode = productCode;
      if (productCode === ProductCodeKey.ScreeningKit) {
        finalProductCode = ProductCodeKey.VMSEssential;
      }

      const item = new CartItem(finalProductCode, finalProductCode, price, quantity, CartItemType.OneOffPurchase, editable);
      item.unique = isCartItemUnique(productCode);
      return item;
    }
    case CartItemType.Subscription: {
      if (!subscriptionPlans && !selectedPlan) {
        // Create a basic subscription item when plans are missing
        const item = new CartItem(
          `subscription_${productCode}`,
          productCode,
          price,
          quantity,
          CartItemType.Subscription,
          editable,
          undefined,
          undefined,
          true,
          undefined,
          undefined,
          [],
          boxItems
        );

        // Add subscription-specific properties
        Object.assign(item, {
          selectedPlan: null,
          subscriptionType: null,
          billing: null,
          delivery: null,
          subscriptionPlans: subscriptionPlans || [],
          subscriptionInterval: null
        });

        return item;
      }

      let dependantItems: CartItem[] = [];

      if (productCode === ProductCodeKey.TamponBox && selectedPlan) {
        const pouch = new CartItem('tampon_pouch_free', ProductCodeKey.TamponPouch, 0);
        pouch.originalPrice = prices?.tamponPouchPrice;
        pouch.editable = false;
        pouch.unique = true;
        dependantItems = [pouch];

        if (selectedPlan?.freeItem && selectedPlan?.freeItem.key === 'free-case') {
          const tin = new CartItem('tin_case_free', ProductCodeKey.TamponTin, 0);
          tin.originalPrice = prices?.tamponTinPrice;
          tin.editable = false;
          tin.unique = true;
          dependantItems = [...dependantItems, tin];
        }
      }

      const item = new CartItem(
        `subscription_${selectedPlan?.productCode || productCode}`,
        productCode,
        selectedPlan?.billing?.installmentPrice ?? price,
        quantity,
        CartItemType.Subscription,
        editable,
        undefined,
        undefined,
        true,
        undefined,
        undefined,
        dependantItems,
        boxItems
      );

      // Add subscription-specific properties
      if (selectedPlan) {
        Object.assign(item, {
          subscriptionInterval: {
            length: selectedPlan.subscriptionInterval.length,
            unit: selectedPlan.subscriptionInterval.unit
          },
          selectedPlan,
          subscriptionType: selectedPlan.subscriptionType,
          billing: { installments: selectedPlan.billing.installments },
          delivery: { installments: selectedPlan.delivery.installments },
          subscriptionPlans: subscriptionPlans || []
        });
      }

      return item;
    }
    default:
      throw Error('Invalid cart item type.');
  }
};

export const createSimpleCartItemForProductCode = (
  productCode: ProductCodeKey,
  quantity: number,
  price: number,
  editable: boolean,
  type = CartItemType.OneOffPurchase
): CartItem => createCartItemHelper(productCode, quantity, price, editable, type);

export const createCartItem = (
  productCode: ProductCodeKey,
  quantity: number,
  price: number,
  editable: boolean,
  type: CartItemType,
  prices?: Prices,
  boxState?: BoxState | PadsBoxState | PadsBoxStackState | TamponBoxStackState,
  subscriptionPlans?: SubscriptionPlan[],
  selectedPlan?: SubscriptionPlan
): CartItem => {
  let boxItems: BoxItem[] = [];
  let cartIdSuffix = '';

  if (boxState) {
    switch (productCode) {
      case ProductCodeKey.TamponBox:
        boxItems = getTamponBoxStackItems(boxState as TamponBoxStackState, prices);
        break;
      case ProductCodeKey.Pads: {
        const isStackBox = isBoxStatePadsStackBox(boxState as BoxState);

        boxItems = isStackBox ? getPadsStackBoxItems(boxState as PadsBoxStackState, prices) : getPadsBoxItems(boxState as PadsBoxState, prices);
        cartIdSuffix = isStackBox ? '' : generatePadsCartIdSuffix(boxState as PadsBoxState);
        break;
      }
      case ProductCodeKey.ScreeningKit:
        boxItems = getBoxItems(boxState as BoxState);
        cartIdSuffix = `${boxItems[0].name}`;
        break;
      case ProductCodeKey.Proviotics:
        boxItems = getProvioticsBoxItems(selectedPlan);
        break;
      default:
        boxItems = [
          {
            count: 1,
            key: productCode,
            name: selectedPlan?.productCode ?? ''
          }
        ];
    }
  }

  return createCartItemHelper(productCode, quantity, price, editable, type, boxItems, cartIdSuffix, subscriptionPlans, selectedPlan, prices);
};

export const hasTamponsAndPads = (item: CartItem) => {
  const itemContainsPads = (boxItem: BoxItem) =>
    boxItem.count > 0 && (boxItem.key === REGULAR_PAD_KEY || boxItem.key === NIGHT_PAD_KEY || boxItem.key === NIGHT_PLUS_PAD_KEY);

  if (item.productCode === ProductCodeKey.TamponBox && item?.boxItems?.length > 0) {
    const pads = item.boxItems?.filter(itemContainsPads);
    return pads.length > 0;
  }
  return false;
};

const retailBoxesKeys = [
  TAMPON_BOX_9_CBD_REGULAR_KEY,
  TAMPON_BOX_9_CBD_SUPER_KEY,
  TAMPON_BOX_18_NUDE_REGULAR_KEY,
  TAMPON_BOX_18_NUDE_SUPER_KEY,
  TAMPON_BOX_9_NUDE_REGULAR_KEY,
  TAMPON_BOX_9_NUDE_SUPER_KEY,
  TAMPON_BOX_9_NUDE_REGULAR_9_CBD_REGULAR_KEY,
  TAMPON_BOX_9_NUDE_SUPER_9_CBD_SUPER_KEY
];
const tamponsKeys = [CBD_SUPER_TAMPON_KEY, CBD_REGULAR_TAMPON_KEY, NAKED_SUPER_TAMPON_KEY, NAKED_REGULAR_TAMPON_KEY];
const padsKeys = [REGULAR_PAD_KEY, NIGHT_PAD_KEY, NIGHT_PLUS_PAD_KEY, LINER_PAD_KEY];

const boxItemsReducer = (previousValue, currentValue, keySet: Array<string>) => {
  if (currentValue.key && keySet.includes(currentValue.key)) {
    return previousValue + currentValue.count;
  }
  return previousValue;
};

export const getBoxItemsCount = (boxItems: BoxItem[]) =>
  boxItems.reduce((a, b) => boxItemsReducer(a, b, tamponsKeys.concat(padsKeys).concat(retailBoxesKeys)), 0);
export const getBoxTamponsCount = (boxItems: BoxItem[]) => boxItems.reduce((a, b) => boxItemsReducer(a, b, tamponsKeys), 0);
export const getBoxPadsCount = (boxItems: BoxItem[]) => boxItems.reduce((a, b) => boxItemsReducer(a, b, padsKeys), 0);

export const filterBoxItemTampons = (boxItems: BoxItem[]) => boxItems.filter((item) => tamponsKeys.includes(item.key || ''));
export const filterBoxItemPads = (boxItems: BoxItem[]) => boxItems.filter((item) => padsKeys.includes(item.key || ''));

/**
 * Combines a cartItem with addOnItems based on specific conditions, particularly focusing on the combination of pads and liners.
 * This function tracks whether any actual combination has occurred and returns the original cartItem and addOnItems unchanged if no combination is done.
 * This behavior ensures that the function can be seamlessly integrated into workflows where preserving the original state of items is crucial unless modifications are explicitly required.
 * This is only be applicable to tampon or pads boxes at the moment, and only until we introduce pre-packed boxes and not have customisation down to the tampon/pad count.
 *
 * @param {CartItem} cartItem - The main cart item, potentially to be combined with add-on items.
 * @param {CartItem[]} addOnItems - An array of additional items that may be combined with the main cart item based on certain conditions.
 * @param {CurrencyObj} currency - The currency object for pricing adjustments.
 * @param {CountryCode} country - The country code, used for any pricing calculations that may depend on the shopper's location.
 * @returns {Promise<{combinedCartItem: CartItem, remainingAddOnItems: CartItem[]}>} - An object containing the potentially modified main cart item and any remaining add-on items that were not combined. If no items were combined, the original cartItem and addOnItems are returned unchanged.
 */
export const combineCartItemsAndAddOns = async (cartItem: CartItem, addOnItems: CartItem[], currency: CurrencyObj, country: CountryCode) => {
  let isCombined = false;

  if (cartItem.productCode === ProductCodeKey.Pads) {
    if (addOnItems.length > 0) {
      const combinedCartItem = { ...cartItem };
      const remainingAddOnItems: CartItem[] = [];
      let additionalPrice = 0;

      const promises = addOnItems.map(async (addOnItem: CartItem) => {
        if (!combinedCartItem.boxItems) return;
        if (addOnItem.productCode === ProductCodeKey.PadsBox12L) {
          isCombined = true;
          const padsCount = 12;
          const plItem = combinedCartItem.boxItems.find((item) => item.key === LINER_PAD_KEY);
          const linerPrice = (await getBoxPrice({ pl: 1 } as Variant, currency, country, ProductCodeKey.Pads)) ?? LINER_PAD_PRICE_DEFAULT;
          if (plItem) {
            plItem.count += padsCount;
          } else {
            combinedCartItem.boxItems.push({ count: padsCount, key: LINER_PAD_KEY, name: LINER_PAD_KEY, price: linerPrice });
          }
          additionalPrice += padsCount * linerPrice;
          combinedCartItem.id += `_${padsCount}pl`;
          combinedCartItem.price += additionalPrice;
        } else {
          remainingAddOnItems.push(addOnItem);
        }
      });

      await Promise.all(promises);

      if (isCombined) {
        return { combinedCartItem, remainingAddOnItems };
      }
    }
  }

  return { combinedCartItem: cartItem, remainingAddOnItems: addOnItems };
};

/**
 * Creates a CartItem from a box item, preserving properties from the parent cart item.
 */
const createDividedCartItem = (boxItem: BoxItem, parentCartItem: CartItem, productCode: ProductCodeKey): CartItem => {
  return new CartItem(
    productCode,
    productCode,
    boxItem.price ?? 0,
    boxItem.count,
    parentCartItem.type,
    parentCartItem.editable,
    parentCartItem.giftType,
    parentCartItem.originalPrice,
    parentCartItem.unique,
    parentCartItem.originalShippingFee,
    parentCartItem.shippingFee,
    parentCartItem.dependantCartItems,
    [] // boxItems (empty for divided items)
  );
};

/**
 * Divides a cart item into multiple individual cart items based on its box items.
 * This is used for stack box products (pads and tampons) where each box item becomes a separate cart item.
 *
 * @param cartItem - The cart item to divide
 * @param country - The country code to determine if stack box logic applies
 * @returns An array of cart items (divided items or the original item if division is not applicable)
 */
export const divideCartItems = (cartItem: CartItem, country: CountryCode): CartItem[] => {
  // Only divide non-subscription items with box items
  if (cartItem.type === CartItemType.Subscription || !cartItem.boxItems) {
    return [cartItem];
  }

  if (isPadsAndStackBox(cartItem.productCode, country)) {
    return cartItem.boxItems
      .filter((boxItem) => boxItem.key && boxItem.price && boxItem.count > 0)
      .map((boxItem) => {
        const productCode = PAD_STACK_PRODUCT_IDS[boxItem.key as PadSizeType];
        return createDividedCartItem(boxItem, cartItem, productCode);
      });
  }

  if (cartItem.productCode === ProductCodeKey.TamponBox) {
    return cartItem.boxItems
      .filter((boxItem) => boxItem.name && boxItem.count > 0)
      .map((boxItem) => {
        const productCode = boxItem.name as ProductCodeKey;
        return createDividedCartItem(boxItem, cartItem, productCode);
      });
  }

  return [cartItem];
};

/**
 * Determines the type of cart item based on the provided product code.
 *
 * @param {ProductCodeKey} productCode - The product code to analyze.
 * @returns {CartItemType} The corresponding cart item type.
 *
 */
export const getCartItemType = (productCode: ProductCodeKey): CartItemType => {
  if (productCode.includes('consultation_')) {
    return CartItemType.Consultation;
  }

  if (productCode === ProductCodeKey.GiftCard) {
    return CartItemType.GiftCard;
  }

  return CartItemType.OneOffPurchase;
};

export const convertBoxItems = (boxItems: BoxItem[] | undefined, productCode: ProductCodeKey, country: CountryCode) => {
  if (!boxItems) {
    return [];
  }

  const filteredBoxItems = boxItems?.filter((bi) => bi.key != null) ?? [];

  if (isPadsAndStackBox(productCode, country)) {
    return convertPadsStackBoxItems(filteredBoxItems);
  }

  return filteredBoxItems;
};

/**
 * Generates line items for Apple Pay based on cart items, shipping cost, and tax.
 * @param {CartItem[]} cartItems - The items in the cart.
 * @param {number} shippingPrice - The cost of shipping.
 * @param {number} tax - The tax amount.
 * @param {TFunction} t - Translation function.
 * @returns {Array<{label: string, amount: string}>} The line items for Apple Pay.
 */
export const getApplePayLineItems = (cartItems: CartItem[], shippingPrice: number, tax: number, t: TFunction, discount = 0) => {
  const lineItems = [];
  cartItems.forEach((item) => {
    const productName = getDBProductName(item.productCode);
    if (item.type === CartItemType.Subscription) {
      const subscriptionItem = item as SubscriptionCartItem;
      const billingInterval = {
        paymentTiming: 'recurring',
        recurringPaymentIntervalUnit: subscriptionItem.subscriptionInterval.unit === IntervalUnit.Months ? 'month' : 'day',
        recurringPaymentIntervalCount: subscriptionItem.subscriptionInterval.length
      };
      lineItems.push({
        label: `${productName}`,
        amount: item.price.toFixed(2),
        ...billingInterval
      });
    } else {
      lineItems.push({ label: productName, amount: item.price.toFixed(2) });
    }
  });
  if (shippingPrice > 0) {
    lineItems.push({ label: t('components.shipping'), amount: shippingPrice.toFixed(2) });
  }
  if (tax > 0) {
    lineItems.push({ label: t('components.tax'), amount: tax.toFixed(2) });
  }
  if (discount > 0) {
    lineItems.push({ label: t('components.discount'), amount: `-${discount.toFixed(2)}` });
  }
  return lineItems;
};

interface GooglePayDisplayItem {
  label: string;
  price: string;
  type: string;
  status: string;
}

/**
 * Generates line items for Google Pay based on cart items, shipping cost, and tax.
 * @param {CartItem[]} cartItems - The items in the cart.
 * @param {number} shippingPrice - The cost of shipping.
 * @param {number} tax - The tax amount.
 * @param {TFunction} t - Translation function.
 * @returns {GooglePayDisplayItem[]} The display items data for Google Pay.
 */
export const getGooglePayLineItems = (
  cartItems: CartItem[],
  shippingPrice: number,
  tax: number,
  t: TFunction,
  discount = 0
): GooglePayDisplayItem[] => {
  const lineItems = cartItems.map((item) => {
    const productName = getDBProductName(item.productCode);
    return {
      label: productName,
      price: item.price.toFixed(2),
      type: 'LINE_ITEM',
      status: 'FINAL'
    };
  });

  if (shippingPrice > 0) {
    lineItems.push({ label: t('components.shipping'), price: shippingPrice.toFixed(2), type: 'LINE_ITEM', status: 'FINAL' });
  }

  if (tax > 0) {
    lineItems.push({ label: t('components.tax'), price: tax.toFixed(2), type: 'LINE_ITEM', status: 'FINAL' });
  }

  if (discount > 0) {
    lineItems.push({ label: t('components.discount'), price: `-${discount.toFixed(2)}`, type: 'LINE_ITEM', status: 'FINAL' });
  }

  return lineItems;
};

export const hasSubscriptionCartItems = (cartItems: CartItems): boolean => {
  return cartItems.some((item) => item.type === CartItemType.Subscription);
};

export const hasEligibleCartItems = (cartItems: CartItems, country: CountryCode): boolean => {
  return cartItems.some((item) => isHsaFsaAvailable(country, item.id as ProductCodeKey));
};

/**
 * Calculates the total quantity of "paid" cart items.
 *
 * This function iterates over an array of cart items and sums the quantities
 * of items that have a price greater than 0. Items with a zero (or falsy) price
 * are considered free and are not counted.
 *
 * @param {CartItem[]} cartItems - The array of cart items to calculate the paid count from.
 * @returns {number} The total quantity of paid cart items.
 */
export function calculatePaidCartItemsCount(cartItems: CartItem[]): number {
  return cartItems.reduce((total: number, cartItem: CartItem) => {
    if (cartItem.price > 0) {
      return total + (cartItem.quantity ?? 0);
    }
    return total;
  }, 0);
}

/**
 * Updates the cart session with the provided cart items.
 * @param {CartItems} cartItems - The cart items to update the session with.
 */
export function updateCartSession(cartItems: CartItems) {
  setOnLocalStorage('cartItems', cartItems, true);
}

/**
 * Generates a unique cart ID for a product code.
 * @param {AppState} state - The current state of the app.
 * @param {string} productCode - The product code to generate a unique ID for.
 * @returns {string} The unique cart ID.
 */
export function getUniqueCartId(state: AppState, productCode: string) {
  const existingItemsForProductCode = state.cartItems.filter((item: CartItem) => item.productCode === productCode);
  const maxNumber = existingItemsForProductCode.reduce((max, item) => {
    const idNumber = parseInt(item.id.split('-')[1], 10);
    return Math.max(max, idNumber);
  }, 0);
  const newNumber = maxNumber + 1;
  return `${productCode}-${newNumber}`;
}

/**
 * Retrieves the dependant cart items for a given cart item.
 * @param {CartItem} cartItem - The cart item to retrieve dependant cart items for.
 * @returns {CartItem[]} The dependant cart items.
 */
export function getDependantCartItems(cartItem: CartItem) {
  return cartItem?.dependantCartItems ?? [];
}

/**
 * Combines dependant cart items and dependant free gifts.
 * @param {CartItem[]} dependantCartItems - The dependant cart items.
 * @param {CartItem[]} dependantFreeGifts - The dependant free gifts.
 * @returns {CartItem[]} The combined dependant cart items and dependant free gifts.
 */
export function combineDependantItems(dependantCartItems: CartItem[], dependantFreeGifts: CartItem[]) {
  return [...dependantCartItems, ...dependantFreeGifts];
}

/**
 * Updates the dependant cart items for a given cart item.
 * @param {CartItem} cartItem - The cart item to update the dependant cart items for.
 * @param {CartItem[]} combinedDependantItems - The combined dependant cart items and dependant free gifts.
 */
export function updateDependantCartItems(cartItem: CartItem, combinedDependantItems: CartItem[]) {
  // eslint-disable-next-line no-param-reassign
  cartItem.dependantCartItems = combinedDependantItems;
}

/**
 * Processes dependant cart items for a given cart item.
 * @param {AppState} state - The current state of the app.
 * @param {CartItem[]} adjustedCartItems - The adjusted cart items.
 * @param {CartItem} cartItem - The cart item to process the dependant cart items for.
 * @returns {CartItem[]} The processed cart items.
 */
export function processDependantCartItems(state: AppState, adjustedCartItems: CartItem[], cartItem: CartItem) {
  let result = adjustedCartItems;
  cartItem.dependantCartItems?.forEach((ci) => {
    result = handleCartItemAdd(state, result, ci, false);
  });

  return result;
}

export function handleCartItemAdd(state: AppState, cartItems: CartItem[], cartItem: CartItem, trackItems: Boolean = true) {
  const email = state?.firebaseAuth?.currentUser?.email ?? '';
  let adjustedCartItems = [...cartItems];
  const existingCartItem = adjustedCartItems.find((item: CartItem) => item.id === cartItem.id && item.type === cartItem.type);

  // Create a deep copy of the cart item with all properties
  const itemToAdd = new CartItem(
    cartItem.id,
    cartItem.productCode,
    cartItem.price,
    cartItem.quantity,
    cartItem.type,
    cartItem.editable,
    cartItem.giftType,
    cartItem.originalPrice,
    cartItem.unique,
    cartItem.originalShippingFee,
    cartItem.shippingFee,
    cartItem.dependantCartItems
      ? cartItem.dependantCartItems.map(
          (dep) =>
            new CartItem(
              dep.id,
              dep.productCode,
              dep.price,
              dep.quantity,
              dep.type,
              dep.editable,
              dep.giftType,
              dep.originalPrice,
              dep.unique,
              dep.originalShippingFee,
              dep.shippingFee,
              dep.dependantCartItems,
              dep.boxItems ? dep.boxItems.map((item) => ({ ...item })) : undefined
            )
        )
      : undefined,
    cartItem.boxItems ? cartItem.boxItems.map((item) => ({ ...item })) : undefined
  );

  // Copy any additional subscription-specific properties if they exist
  if (cartItem.type === CartItemType.Subscription) {
    const subscriptionItem = cartItem as any;
    Object.assign(itemToAdd, {
      selectedPlan: subscriptionItem.selectedPlan,
      subscriptionType: subscriptionItem.subscriptionType,
      billing: subscriptionItem.billing,
      delivery: subscriptionItem.delivery,
      subscriptionPlans: subscriptionItem.subscriptionPlans,
      subscriptionInterval: subscriptionItem.subscriptionInterval
    });
  }

  // T043: Handle donation items with unique=true (only one donation per cart)
  if (cartItem.type === CartItemType.Donation) {
    // Copy metadata for donation tracking
    (itemToAdd as any).metadata = (cartItem as any).metadata;

    // Remove any existing donation items
    adjustedCartItems = adjustedCartItems.filter((item: CartItem) => item.type !== CartItemType.Donation);

    // Add the new donation item
    adjustedCartItems.push(itemToAdd);
  } else if (existingCartItem?.type === CartItemType.Subscription) {
    adjustedCartItems = handleCartItemRemove(state, cartItems, itemToAdd);
  }

  if (!existingCartItem || existingCartItem?.type === CartItemType.Subscription) {
    // adding new cart item (skip if already handled above for donations)
    if (cartItem.type !== CartItemType.Donation) {
      adjustedCartItems.push(itemToAdd);
    }
  } else if (existingCartItem.editable && !cartItem.editable) {
    adjustedCartItems = handleCartItemRemove(state, cartItems, existingCartItem);
    adjustedCartItems = handleCartItemAdd(state, adjustedCartItems, itemToAdd);
  } else if (cartItem.price > 0) {
    // increasing cart item quantity
    existingCartItem.quantity += itemToAdd.quantity;
  }

  const newPrice = calculateTotalPrice(adjustedCartItems);

  // Handle dependent items
  const dependantCartItems = getDependantCartItems(itemToAdd);
  const dependantFreeGifts = getDependantFreeGifts(itemToAdd, state.country, state.currency, state.price);
  const combinedDependantItems = combineDependantItems(dependantCartItems, dependantFreeGifts);
  updateDependantCartItems(itemToAdd, combinedDependantItems);
  adjustedCartItems = processDependantCartItems(state, adjustedCartItems, itemToAdd);

  // Handle free gifts
  const freeGifts = getFreeGiftsForPromotion(itemToAdd, state.country, state.currency, newPrice);
  const newFreeGifts = freeGifts.filter((gift) => !adjustedCartItems.some((item) => item.id === gift.id));

  if (trackItems) addToCartTrack(itemToAdd, email);

  adjustedCartItems.push(...newFreeGifts);

  // Update cart session but don't dispatch - that should be done by the caller
  if (JSON.stringify(adjustedCartItems) !== JSON.stringify(state.cartItems)) {
    updateCartSession(adjustedCartItems);
  }

  return adjustedCartItems;
}

export function handleCartItemRemove(state: AppState, cartItems: CartItem[], itemToRemove: CartItem) {
  const email = state?.firebaseAuth?.currentUser?.email ?? '';

  const itemToBeRemoved = cartItems.find((item: CartItem) => item.id === itemToRemove.id && item.type === itemToRemove.type);
  let adjustedItems = [...cartItems];
  if (itemToBeRemoved) {
    if (itemToBeRemoved.editable || itemToBeRemoved.quantity - 1 <= 0) {
      // removing cart item
      adjustedItems = cartItems.filter((item: CartItem) => item.id !== itemToBeRemoved.id || item.type !== itemToBeRemoved.type);
    } else if (itemToBeRemoved.quantity - 1 > 0) {
      // decreasing cart item quantity but keeping it in the cart
      itemToBeRemoved.quantity -= 1;
    }
    removeFromCartTrack(itemToBeRemoved, email);
  }
  if (itemToBeRemoved && itemToBeRemoved.dependantCartItems) {
    itemToBeRemoved.dependantCartItems.forEach((di) => {
      adjustedItems = handleCartItemRemove(state, adjustedItems, di);
    });
  }

  const newPrice = calculateTotalPrice(adjustedItems);
  adjustedItems = removeInvalidFreeGifts(adjustedItems, { ...state, price: newPrice }, itemToRemove);

  return adjustedItems;
}

/**
 * Handles the removal of dependent cart items from the cart.
 * @param {CartItem[]} cartItems - The array of cart items.
 * @param {CartItem} itemToRemove - The item to remove.
 * @returns {CartItem[]} The updated cart items.
 */
export function handleCartItemDependentItemsRemove(cartItems: CartItem[], itemToRemove: CartItem) {
  const itemIndex = cartItems.findIndex((item) => item.id === itemToRemove.id && item.type === itemToRemove.type);
  if (itemIndex !== -1 && cartItems[itemIndex].dependantCartItems) {
    const newCartItems = [...cartItems];
    newCartItems[itemIndex] = { ...newCartItems[itemIndex], dependantCartItems: undefined };
    return newCartItems;
  }
  return [...cartItems];
}
export function createFreeGiftWithProductCode(productCode: ProductCodeKey, country: CountryCode, currency: CurrencyObj, type: CartItemGiftType) {
  let originalPrice = 0;
  const pricesData = getFromLocalStorage('productPrices', true);
  if (pricesData && Array.isArray(pricesData)) {
    const product = pricesData.find((itm) => itm.id === productCode);
    if (product && product.price) {
      const vatPercent = product?.vat[country] ?? 0;
      const price = product.price.find((p) => p.currency === currency.letterCode);
      if (price) {
        const vatAmount = price.amount * (vatPercent / 100);
        const finalPrice = price.amount + vatAmount;
        originalPrice = finalPrice;
      }
    }
  }
  const cartItemType = getCartItemType(productCode);
  return new CartItem(`${productCode}_free`, productCode, 0, 1, cartItemType, false, type, originalPrice);
}

export function removeInvalidFreeGifts(cartItems: CartItem[], state: AppState, cartItem: CartItem): CartItem[] {
  let updatedCartItems = [...cartItems];
  const freeGiftItems = updatedCartItems.filter((item) => item.giftType === CartItemGiftType.PromotionCode);

  freeGiftItems.forEach((freeGift) => {
    const validGifts = getFreeGiftsForPromotion(cartItem, state.country, state.currency, state.price);

    const isStillValid = validGifts.some((validGift) => validGift.productCode === freeGift.productCode);

    if (!isStillValid) {
      updatedCartItems = handleCartItemRemove(state, updatedCartItems, freeGift);
    }
  });

  return updatedCartItems;
}

export function validateCartPromotions(state: AppState, dispatch: Dispatch<MainContextActionTypes>) {
  let adjustedItems = [...state.cartItems];

  // First, remove any invalid free gifts based on current cart state
  state.cartItems.forEach((cartItem) => {
    if (cartItem.price > 0) {
      adjustedItems = removeInvalidFreeGifts(adjustedItems, state, cartItem);
    }
  });

  const allValidGifts: CartItem[] = [];

  // Process each non-gift item for potential promotions
  state.cartItems.forEach((cartItem) => {
    if (cartItem.price > 0 && cartItem.giftType === undefined) {
      const itemValidGifts = getFreeGiftsForPromotion(cartItem, state.country, state.currency, state.price);

      itemValidGifts.forEach((gift) => {
        if (!allValidGifts.some((existingGift) => existingGift.id === gift.id)) {
          allValidGifts.push(gift);
        }
      });
    }
  });

  const newFreeGifts = allValidGifts.filter((gift) => !adjustedItems.some((item) => item.id === gift.id));

  adjustedItems = [...adjustedItems, ...newFreeGifts];

  if (JSON.stringify(adjustedItems) !== JSON.stringify(state.cartItems)) {
    updateCartSession(adjustedItems);
    dispatch({
      type: 'REHYDRATE-FROM-SESSION-STORAGE',
      data: {
        ...state,
        cartItems: adjustedItems
      }
    });
  }
}

/**
 * Creates box items for Proviotics subscriptions.
 *
 * This function generates the appropriate BoxItem array for a Proviotics subscription
 * based on the selected subscription plan. It sets the count to the subscription interval
 * length, which represents the number of months in the subscription.
 *
 * @param {SubscriptionPlan} selectedPlan - The subscription plan containing interval information
 * @returns {BoxItem[]} An array containing a single BoxItem with the Proviotics product details
 */
export const getProvioticsBoxItems = (selectedPlan?: SubscriptionPlan): BoxItem[] => {
  return [
    {
      count: selectedPlan?.subscriptionInterval.length ?? 1,
      key: ProductCodeKey.Proviotics,
      name: selectedPlan?.productCode ?? ''
    }
  ];
};
